import { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import {
  adderResumeSelector,
  attributeSlotSelector,
  changeSelectedAttributes,
  currentProductAdderStatus,
  loadingTariffGroupAttributesSelector,
  productAttributeTypesSelector,
  selectedProductAttributesSelector,
  tariffsState,
} from '@src/stores/product';

import type { AttributeOption, TariffGroupAttributeType } from '../types/attribute';
import { attributeSlot } from '../types/productBookingSlot';
import { getAttributeTypeLabel } from '../core/attributes/getAttributeTypeLabel';
import { getSelectedAttributeValue } from '../core/attributes/selectedAttributes';
import { trackStepConfirmed } from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';

const EMPTY_OPTIONS: AttributeOption[] = [];

type UseAttributeSelectorReturn = {
  visible: boolean;
  loading: boolean;
  label: string;
  options: AttributeOption[];
  selectedValue: string | null;
  selectedOption: AttributeOption | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onSelect: (option: AttributeOption | null) => void;
  isAddingToCart: boolean;
};

export function useAttributeSelector(type: TariffGroupAttributeType): UseAttributeSelectorReturn {
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();

  const { productId } = useAppSelector(adderResumeSelector(widgetId));
  const loading = useAppSelector(loadingTariffGroupAttributesSelector(productId ?? ''));
  const selectedAttributes = useAppSelector(selectedProductAttributesSelector(widgetId));
  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const slot = useAppSelector(attributeSlotSelector(widgetId, type));
  const productAttributeTypes = useAppSelector(productAttributeTypesSelector(productId ?? ''));

  const [open, setOpen] = useState(false);

  const options = slot?.options ?? EMPTY_OPTIONS;
  const label = useMemo(() => getAttributeTypeLabel(type, (key) => i18n(key)), [type, i18n]);

  const selectedValue = useMemo(() => getSelectedAttributeValue(selectedAttributes, type), [selectedAttributes, type]);

  const selectedOption = useMemo(
    () => (selectedValue ? options.find((option) => option.value === selectedValue) ?? null : null),
    [options, selectedValue],
  );

  const onOpenChange = useCallback((next: boolean) => {
    setOpen(next);
  }, []);

  const onSelect = useCallback(
    (option: AttributeOption | null) => {
      if (!option) {
        return;
      }

      dispatch(changeSelectedAttributes({ id: widgetId, value: [{ type, value: option.value }] }));
      trackStepConfirmed(widgetId, productId ?? '', attributeSlot(type));
      setOpen(false);
    },
    [dispatch, widgetId, productId, type],
  );

  const isAddingToCart = currentStatus === 'adding_to_cart';

  const visible = useMemo(() => {
    if (loading) return true;

    if (!selectedDate) {
      return productAttributeTypes.includes(type);
    }

    return options.length > 0;
  }, [loading, selectedDate, productAttributeTypes, type, options]);

  return {
    visible,
    loading,
    label,
    options,
    selectedValue,
    selectedOption,
    open,
    onOpenChange,
    onSelect,
    isAddingToCart,
  };
}
